import {
  IScheduleHandler,
  ScheduleContext,
  ScheduleEvaluationResult,
  SchedulerConfiguration,
  EnhancedSchedulerResult,
  ActivePlaylist,
  PlaylistScheduleStatus,
  ConflictResolution,
  DEFAULT_SCHEDULER_CONFIG,
  PriorityLevel,
} from "../types/index";
import { Playlist, ScheduleType, ensureScheduleParsed } from "../types";
import { PrayerTimes } from "../types/PrayerTypes";
import { ScreenData } from "../types/ScreenTypes";

// Import all schedule handlers
import { AlwaysScheduleHandler } from "./AlwaysScheduleHandler";
import { TimeWindowScheduleHandler } from "./TimeWindowScheduleHandler";
import { CrossDayTimeWindowScheduleHandler } from "./CrossDayTimeWindowScheduleHandler";
import { PrayerWindowScheduleHandler } from "./PrayerWindowScheduleHandler";
import { CrossDayPrayerWindowScheduleHandler } from "./CrossDayPrayerWindowScheduleHandler";
import { HijriDateScheduleHandler } from "./HijriDateScheduleHandler";
import { GregorianDateScheduleHandler } from "./GregorianDateScheduleHandler";
import { PrayerBasedScheduleHandler } from "./PrayerBasedScheduleHandler";

/**
 * Enhanced Playlist Scheduler
 *
 * Centralized scheduling engine that evaluates all playlists and determines
 * which should be active based on their schedule configurations.
 *
 * Features:
 * - Supports 8 schedule types (Always, TimeWindow, PrayerWindow, etc.)
 * - Priority-based conflict resolution
 * - Prayer time integration
 * - Efficient evaluation with caching
 * - Detailed logging and debugging
 *
 * Azure Best Practice: Service layer pattern for complex business logic
 */
export class EnhancedPlaylistScheduler {
  private handlers: Map<ScheduleType, IScheduleHandler>;
  public readonly config: SchedulerConfiguration;
  private evaluationCache: Map<
    string,
    { result: ScheduleEvaluationResult; timestamp: number }
  >;
  private readonly CACHE_TTL_MS = 1000; // change to 1s

  constructor(config: Partial<SchedulerConfiguration> = {}) {
    this.config = { ...DEFAULT_SCHEDULER_CONFIG, ...config };
    this.handlers = new Map();
    this.evaluationCache = new Map();
    this.registerHandlers();
  }

  /**
   * Register all schedule type handlers
   * Azure Best Practice: Dependency injection pattern for extensibility
   */
  private registerHandlers(): void {
    // Register handlers for each schedule type
    const handlers: IScheduleHandler[] = [
      new AlwaysScheduleHandler(), // Type 0
      new TimeWindowScheduleHandler(), // Type 1
      new PrayerWindowScheduleHandler(), // Type 2
      new CrossDayTimeWindowScheduleHandler(), // Type 3
      new CrossDayPrayerWindowScheduleHandler(), // Type 4
      new HijriDateScheduleHandler(), // Type 5
      new GregorianDateScheduleHandler(), // Type 6
      new PrayerBasedScheduleHandler(), // Type 7
    ];

    handlers.forEach((handler) => {
      this.handlers.set(handler.scheduleType, handler);
    });
  }

  /**
   * Evaluate all playlists and return active ones with priority resolution
   *
   * Azure Best Practice: Single responsibility - focused on playlist evaluation
   *
   * @param playlists List of playlists to evaluate
   * @param prayerTimes Current prayer times (optional, required for prayer-based schedules)
   * @param currentTime Current time (defaults to now)
   * @param screenData Screen data for evaluation context (optional)
   * @returns Comprehensive scheduler result with active playlists and metadata
   */
  public evaluatePlaylists(
    playlists: Playlist[],
    prayerTimes?: PrayerTimes,
    currentTime: Date = new Date(),
    screenData?: ScreenData, // Add this parameter
  ): EnhancedSchedulerResult {
    const startTime = Date.now();

    // Log screenData being used
    if (this.config.enableLogging) {
    }

    const context: ScheduleContext = {
      currentTime,
      prayerTimes,
      screenData: screenData || this.createDefaultScreenData(), // Add to context
    };

    // Evaluate each playlist
    const evaluations: Map<number, ScheduleEvaluationResult> = new Map();
    const statuses: PlaylistScheduleStatus[] = [];

    for (const playlist of playlists) {
      // ✅ FIX: Ensure schedule is parsed before evaluation
      const playlistWithSchedule = ensureScheduleParsed(playlist);
      const evaluation = this.evaluatePlaylist(playlistWithSchedule, context);
      evaluations.set(playlist.id, evaluation);

      const isDisplayEligible = this.isUrgentDisplayEligible(
        playlistWithSchedule,
        currentTime,
      );

      statuses.push({
        playlistId: playlist.id,
        playlistName: playlist.name,
        scheduleType:
          playlistWithSchedule.schedule?.type ?? ScheduleType.Always, // ✅ FIX: Access nested type
        isActive: evaluation.isActive && isDisplayEligible,
        reason: isDisplayEligible
          ? evaluation.reason
          : "Urgent playlist expired or invalid",
        effectivePriority: evaluation.effectivePriority,
        deactivationTime: evaluation.deactivationTime,
        nextEvaluationTime: evaluation.nextEvaluationTime,
      });
    }

    // Get active playlists (use original playlists for further processing)
    const activePlaylists = playlists.filter((playlist) => {
      const evaluation = evaluations.get(playlist.id);
      if (!evaluation?.isActive) {
        return false;
      }

      // An empty playlist has nothing to show, so it never takes the screen (e.g. an exclusive one waiting for posters)
      if (!playlist.items?.length) {
        return false;
      }

      return this.isUrgentDisplayEligible(playlist, currentTime);
    });

    // Resolve conflicts and prioritize
    const { selectedPlaylists, conflicts } = this.resolveConflicts(
      activePlaylists,
      evaluations,
    );

    // Sort by priority (highest first)
    const sortedPlaylists = selectedPlaylists.sort(
      (a, b) => b.effectivePriority - a.effectivePriority,
    );

    const executionTime = Date.now() - startTime;

    return {
      activePlaylists: sortedPlaylists,
      allStatuses: statuses,
      conflictsResolved: conflicts,
      evaluationTime: currentTime,
      nextEvaluationTime: this.calculateNextEvaluation(statuses),
      executionTimeMs: executionTime,
    };
  }

  /**
   * Evaluate a single playlist
   *
   * Azure Best Practice: Caching for performance optimization
   */
  private evaluatePlaylist(
    playlist: Playlist,
    context: ScheduleContext,
  ): ScheduleEvaluationResult {
    // Check cache first
    const cacheKey = this.getCacheKey(playlist.id, context.currentTime);
    const cached = this.evaluationCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL_MS) {
      return cached.result;
    }

    // ✅ FIX: Get schedule type from nested schedule object
    const scheduleType = playlist.schedule?.type ?? ScheduleType.Always;
    const handler = this.handlers.get(scheduleType);

    if (!handler) {
      const result: ScheduleEvaluationResult = {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `No handler found for schedule type ${scheduleType}`,
      };

      return result;
    }

    // Validate configuration before evaluation
    const validationErrors = handler.validateConfiguration(playlist);
    if (validationErrors.length > 0) {
      const result: ScheduleEvaluationResult = {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Configuration errors: ${validationErrors.join(", ")}`,
      };

      return result;
    }

    // Evaluate
    const result = handler.evaluate(playlist, context);

    // Cache result
    this.evaluationCache.set(cacheKey, {
      result,
      timestamp: Date.now(),
    });

    return result;
  }

  /**
   * Resolve conflicts between active playlists based on priority
   *
   * Azure Best Practice: Clear conflict resolution strategy
   */
  private resolveConflicts(
    activePlaylists: Playlist[],
    evaluations: Map<number, ScheduleEvaluationResult>,
  ): { selectedPlaylists: ActivePlaylist[]; conflicts: ConflictResolution[] } {
    const conflicts: ConflictResolution[] = [];
    const selectedPlaylists: ActivePlaylist[] = [];

    if (activePlaylists.length === 0) {
      return { selectedPlaylists, conflicts };
    }

    const now = new Date();

    // Prayer-based critical lane should win over urgent takeover.
    const prayerBasedPlaylists = activePlaylists.filter((playlist) => {
      const parsedPlaylist = ensureScheduleParsed(playlist);
      return parsedPlaylist.schedule?.type === ScheduleType.PrayerBased;
    });

    if (prayerBasedPlaylists.length > 0) {
      const winningPrayerBased = prayerBasedPlaylists.slice().sort((a, b) => {
        if ((b.priority || 0) !== (a.priority || 0)) {
          return (b.priority || 0) - (a.priority || 0);
        }

        const aAssignment = a.assignmentPriority ?? Number.MAX_SAFE_INTEGER;
        const bAssignment = b.assignmentPriority ?? Number.MAX_SAFE_INTEGER;
        if (aAssignment !== bAssignment) {
          return aAssignment - bAssignment;
        }

        return a.id - b.id;
      })[0];

      const evaluation = evaluations.get(winningPrayerBased.id);
      if (evaluation) {
        selectedPlaylists.push({
          playlist: winningPrayerBased,
          effectivePriority: PriorityLevel.Critical,
          deactivationTime: evaluation.deactivationTime,
        });

        for (const playlist of activePlaylists) {
          if (playlist.id !== winningPrayerBased.id) {
            conflicts.push({
              playlistId: playlist.id,
              playlistName: playlist.name,
              conflictReason: "Overridden by prayer-based critical playlist",
              resolutionAction: "skipped",
              winningPlaylistId: winningPrayerBased.id,
            });
          }
        }

        return { selectedPlaylists, conflicts };
      }
    }

    // Option 2: explicit urgent takeover playlists
    const urgentTakeoverPlaylists = activePlaylists.filter((playlist) =>
      this.isUrgentTakeoverActive(playlist, now),
    );

    if (urgentTakeoverPlaylists.length > 0) {
      const winningTakeover = urgentTakeoverPlaylists.slice().sort((a, b) => {
        // Higher playlist priority wins
        if ((b.priority || 0) !== (a.priority || 0)) {
          return (b.priority || 0) - (a.priority || 0);
        }

        // Lower assignment priority wins as tie-break
        const aAssignment = a.assignmentPriority ?? Number.MAX_SAFE_INTEGER;
        const bAssignment = b.assignmentPriority ?? Number.MAX_SAFE_INTEGER;
        if (aAssignment !== bAssignment) {
          return aAssignment - bAssignment;
        }

        // Stable final tie-break
        return a.id - b.id;
      })[0];

      const evaluation = evaluations.get(winningTakeover.id);
      if (evaluation) {
        selectedPlaylists.push({
          playlist: winningTakeover,
          effectivePriority: PriorityLevel.Critical,
          deactivationTime: evaluation.deactivationTime,
        });

        for (const playlist of activePlaylists) {
          if (playlist.id !== winningTakeover.id) {
            conflicts.push({
              playlistId: playlist.id,
              playlistName: playlist.name,
              conflictReason: "Overridden by urgent takeover playlist",
              resolutionAction: "skipped",
              winningPlaylistId: winningTakeover.id,
            });
          }
        }

        return { selectedPlaylists, conflicts };
      }
    }

    const exclusivePlaylists = activePlaylists.filter(
      (playlist) => playlist.isExclusiveWhileActive,
    );

    if (exclusivePlaylists.length > 0) {
      const winningExclusive = exclusivePlaylists.slice().sort((a, b) => {
        if ((b.priority || 0) !== (a.priority || 0)) {
          return (b.priority || 0) - (a.priority || 0);
        }

        const aAssignment = a.assignmentPriority ?? Number.MAX_SAFE_INTEGER;
        const bAssignment = b.assignmentPriority ?? Number.MAX_SAFE_INTEGER;
        if (aAssignment !== bAssignment) {
          return aAssignment - bAssignment;
        }

        return a.id - b.id;
      })[0];

      const evaluation = evaluations.get(winningExclusive.id);
      if (evaluation) {
        selectedPlaylists.push({
          playlist: winningExclusive,
          effectivePriority: evaluation.effectivePriority,
          deactivationTime: evaluation.deactivationTime,
        });

        for (const playlist of activePlaylists) {
          if (playlist.id !== winningExclusive.id) {
            conflicts.push({
              playlistId: playlist.id,
              playlistName: playlist.name,
              conflictReason: "Overridden by exclusive playlist",
              resolutionAction: "skipped",
              winningPlaylistId: winningExclusive.id,
            });
          }
        }

        return { selectedPlaylists, conflicts };
      }
    }

    // Option 2: urgent inject playlists should coexist with normal content
    const urgentInjectPlaylists = activePlaylists.filter((playlist) =>
      this.isUrgentInjectActive(playlist, now),
    );

    if (urgentInjectPlaylists.length > 0) {
      // Include all active inject playlists
      for (const playlist of urgentInjectPlaylists) {
        const evaluation = evaluations.get(playlist.id);
        if (!evaluation) continue;

        selectedPlaylists.push({
          playlist,
          effectivePriority: evaluation.effectivePriority,
          deactivationTime: evaluation.deactivationTime,
        });
      }

      const nonInjectPlaylists = activePlaylists.filter(
        (playlist) => !this.isUrgentInjectActive(playlist, now),
      );

      if (nonInjectPlaylists.length > 0) {
        const priorityGroups = new Map<number, Playlist[]>();

        for (const playlist of nonInjectPlaylists) {
          const evaluation = evaluations.get(playlist.id);
          if (!evaluation) continue;

          const priority = evaluation.effectivePriority;
          const group = priorityGroups.get(priority) || [];
          group.push(playlist);
          priorityGroups.set(priority, group);
        }

        const priorities = Array.from(priorityGroups.keys()).sort(
          (a, b) => b - a,
        );
        const highestPriority = priorities[0];

        if (this.config.enablePriorityOverride) {
          const highestPriorityPlaylists =
            priorityGroups.get(highestPriority) || [];

          for (const playlist of highestPriorityPlaylists) {
            const evaluation = evaluations.get(playlist.id)!;
            selectedPlaylists.push({
              playlist,
              effectivePriority: evaluation.effectivePriority,
              deactivationTime: evaluation.deactivationTime,
            });
          }

          for (let i = 1; i < priorities.length; i++) {
            const lowerPriorityPlaylists =
              priorityGroups.get(priorities[i]) || [];
            for (const playlist of lowerPriorityPlaylists) {
              conflicts.push({
                playlistId: playlist.id,
                playlistName: playlist.name,
                conflictReason: `Lower priority (${priorities[i]}) than active playlists (${highestPriority})`,
                resolutionAction: "skipped",
                winningPlaylistId: highestPriorityPlaylists[0]?.id,
              });
            }
          }
        } else {
          for (const playlist of nonInjectPlaylists) {
            const evaluation = evaluations.get(playlist.id)!;
            selectedPlaylists.push({
              playlist,
              effectivePriority: evaluation.effectivePriority,
              deactivationTime: evaluation.deactivationTime,
            });
          }
        }
      }

      return { selectedPlaylists, conflicts };
    }

    // Check for PrayerBased "after" playlists with deterministic winner selection.
    const afterPrayerPlaylist = this.getWinningPrayerBasedPlaylist(
      activePlaylists,
      ["after", "after-start", "after-end"],
    );

    if (afterPrayerPlaylist) {
      const evaluation = evaluations.get(afterPrayerPlaylist.id);
      if (evaluation) {
        // Add only this playlist with super-high priority
        selectedPlaylists.push({
          playlist: afterPrayerPlaylist,
          effectivePriority: PriorityLevel.Critical, // Super high priority
          deactivationTime: evaluation.deactivationTime,
        });

        // Record all other playlists as conflicts
        for (const playlist of activePlaylists) {
          if (playlist.id !== afterPrayerPlaylist.id) {
            conflicts.push({
              playlistId: playlist.id,
              playlistName: playlist.name,
              conflictReason: "Overridden by after-prayer playlist",
              resolutionAction: "skipped",
              winningPlaylistId: afterPrayerPlaylist.id,
            });
          }
        }

        return { selectedPlaylists, conflicts };
      }
    }

    // 🆕 SECOND check for prayer-based "before" playlists

    const beforePrayerPlaylist = this.getWinningPrayerBasedPlaylist(
      activePlaylists,
      ["before"],
    );

    if (beforePrayerPlaylist) {
      const evaluation = evaluations.get(beforePrayerPlaylist.id);
      if (evaluation) {
        // Add only this playlist to selectedPlaylists
        selectedPlaylists.push({
          playlist: beforePrayerPlaylist,
          effectivePriority: PriorityLevel.Critical, // High priority, but below "after" playlists
          deactivationTime: evaluation.deactivationTime,
        });

        // Record all other playlists as conflicts
        for (const playlist of activePlaylists) {
          if (playlist.id !== beforePrayerPlaylist.id) {
            conflicts.push({
              playlistId: playlist.id,
              playlistName: playlist.name,
              conflictReason: "Overridden by before-prayer playlist",
              resolutionAction: "skipped",
              winningPlaylistId: beforePrayerPlaylist.id,
            });
          }
        }

        return { selectedPlaylists, conflicts };
      }
    }

    // 🆕 FALLBACK - Original conflict resolution logic below

    // Group by priority
    const priorityGroups = new Map<number, Playlist[]>();

    for (const playlist of activePlaylists) {
      const evaluation = evaluations.get(playlist.id);
      if (!evaluation) continue;

      const priority = evaluation.effectivePriority;
      const group = priorityGroups.get(priority) || [];
      group.push(playlist);
      priorityGroups.set(priority, group);
    }

    // Get highest priority
    const priorities = Array.from(priorityGroups.keys()).sort((a, b) => b - a);
    const highestPriority = priorities[0];

    // If priority override is enabled, only use highest priority playlists
    if (this.config.enablePriorityOverride) {
      const highestPriorityPlaylists =
        priorityGroups.get(highestPriority) || [];

      for (const playlist of highestPriorityPlaylists) {
        const evaluation = evaluations.get(playlist.id)!;
        selectedPlaylists.push({
          playlist,
          effectivePriority: evaluation.effectivePriority,
          deactivationTime: evaluation.deactivationTime,
        });
      }

      // Record conflicts for lower priority playlists
      for (let i = 1; i < priorities.length; i++) {
        const lowerPriorityPlaylists = priorityGroups.get(priorities[i]) || [];
        for (const playlist of lowerPriorityPlaylists) {
          conflicts.push({
            playlistId: playlist.id,
            playlistName: playlist.name,
            conflictReason: `Lower priority (${priorities[i]}) than active playlists (${highestPriority})`,
            resolutionAction: "skipped",
            winningPlaylistId: highestPriorityPlaylists[0]?.id,
          });
        }
      }
    } else {
      // Include all active playlists regardless of priority
      for (const playlist of activePlaylists) {
        const evaluation = evaluations.get(playlist.id)!;
        selectedPlaylists.push({
          playlist,
          effectivePriority: evaluation.effectivePriority,
          deactivationTime: evaluation.deactivationTime,
        });
      }
    }

    return { selectedPlaylists, conflicts };
  }

  private getWinningPrayerBasedPlaylist(
    activePlaylists: Playlist[],
    prayerScheduleTypes: string[],
  ): Playlist | undefined {
    const candidates = activePlaylists.filter((playlist) => {
      const parsedPlaylist = ensureScheduleParsed(playlist);
      return (
        parsedPlaylist.schedule?.type === ScheduleType.PrayerBased &&
        !!parsedPlaylist.schedule?.prayerScheduleType &&
        prayerScheduleTypes.includes(parsedPlaylist.schedule.prayerScheduleType)
      );
    });

    if (candidates.length === 0) {
      return undefined;
    }

    return candidates.sort((a, b) => {
      if ((b.priority || 0) !== (a.priority || 0)) {
        return (b.priority || 0) - (a.priority || 0);
      }

      const aAssignment = a.assignmentPriority ?? Number.MAX_SAFE_INTEGER;
      const bAssignment = b.assignmentPriority ?? Number.MAX_SAFE_INTEGER;
      if (aAssignment !== bAssignment) {
        return aAssignment - bAssignment;
      }

      return a.id - b.id;
    })[0];
  }

  private isUrgentModeEnabled(playlist: Playlist): boolean {
    return (
      playlist.urgentMode === "UrgentTakeover" ||
      playlist.urgentMode === "UrgentInject"
    );
  }

  private isUrgentDisplayEligible(playlist: Playlist, now: Date): boolean {
    if (!this.isUrgentModeEnabled(playlist)) {
      return true;
    }

    return this.isUrgentWindowActive(playlist, now);
  }

  private isUrgentWindowActive(playlist: Playlist, now: Date): boolean {
    // Strict policy: urgent playlists must define an explicit expiry.
    if (!playlist.urgentActiveUntilUtc) {
      return false;
    }

    const from = playlist.urgentActiveFromUtc
      ? new Date(playlist.urgentActiveFromUtc)
      : null;
    const until = playlist.urgentActiveUntilUtc
      ? new Date(playlist.urgentActiveUntilUtc)
      : null;

    if (from && Number.isNaN(from.getTime())) {
      return false;
    }

    if (until && Number.isNaN(until.getTime())) {
      return false;
    }

    if (from && now < from) {
      return false;
    }

    if (until && now > until) {
      return false;
    }

    return true;
  }

  private isUrgentTakeoverActive(playlist: Playlist, now: Date): boolean {
    return (
      playlist.urgentMode === "UrgentTakeover" &&
      this.isUrgentWindowActive(playlist, now)
    );
  }

  private isUrgentInjectActive(playlist: Playlist, now: Date): boolean {
    return (
      playlist.urgentMode === "UrgentInject" &&
      this.isUrgentWindowActive(playlist, now)
    );
  }

  /**
   * Calculate when the next evaluation should occur
   */
  private calculateNextEvaluation(
    statuses: PlaylistScheduleStatus[],
  ): Date | undefined {
    const nextTimes = statuses
      .map((s) => s.nextEvaluationTime)
      .filter((t): t is Date => t !== undefined)
      .sort((a, b) => a.getTime() - b.getTime());

    return nextTimes.length > 0 ? nextTimes[0] : undefined;
  }

  /**
   * Generate cache key for playlist evaluation
   */
  private getCacheKey(playlistId: number, currentTime: Date): string {
    // filepath: /Users/imrannomanipersonal/m247digitalsignagev3/src/schedulers/EnhancedPlaylistScheduler.ts
    // Round to nearest 1 second to avoid stale results in short windows
    const rounded = Math.floor(currentTime.getTime() / 1000) * 1000; // round to 1s
    return `${playlistId}_${rounded}`;
  }

  /**
   * Clear evaluation cache
   * Call this when prayer times update or significant time jump occurs
   */
  public clearCache(): void {
    this.evaluationCache.clear();
  }

  /**
   * Get handler for a specific schedule type (for testing/debugging)
   */
  public getHandler(scheduleType: ScheduleType): IScheduleHandler | undefined {
    return this.handlers.get(scheduleType);
  }

  /**
   * Validate all playlist configurations
   * Useful for bulk validation during setup
   */
  public validateAllPlaylists(playlists: Playlist[]): Map<number, string[]> {
    const validationResults = new Map<number, string[]>();

    for (const playlist of playlists) {
      // ✅ FIX: Ensure schedule is parsed
      const playlistWithSchedule = ensureScheduleParsed(playlist);
      const scheduleType =
        playlistWithSchedule.schedule?.type ?? ScheduleType.Always;
      const handler = this.handlers.get(scheduleType);

      if (!handler) {
        validationResults.set(playlist.id, [
          `No handler found for schedule type ${scheduleType}`,
        ]);
        continue;
      }

      const errors = handler.validateConfiguration(playlistWithSchedule);
      if (errors.length > 0) {
        validationResults.set(playlist.id, errors);
      }
    }

    return validationResults;
  }

  /**
   * Create default screen data
   * Used when no screen data is provided in the evaluation context
   */
  private createDefaultScreenData(): ScreenData {
    return {
      // Base properties
      id: 0,
      code: "DEFAULT",
      name: "Default Screen",
      masjidId: 0,
      backgroundTemplate: null,
      backgroundType: "none",
      backgroundUrl: null,
      isActive: true,
      isResponsive: true,
      orientation: "landscape",
      layoutData: null,
      layoutType: "default",
      splitTemplate: null,
      width: 1920,
      height: 1080,
      dateCreated: new Date().toISOString(),
      dateModified: new Date().toISOString(),

      // Prayer Length Settings (in seconds) - defaults based on common practice
      fajrLength: 540, // 9 minutes
      dhuhrLength: 360, // 6 minutes
      asarLength: 360, // 6 minutes
      maghribLength: 360, // 6 minutes
      ishaLength: 360, // 6 minutes
      jummahLength: 1800, // 30 minutes

      // Reminder Settings
      salahCountdownReminder: 300,

      // Makrooh Time Settings (in minutes)
      sunriseMakroohPeriod: 15,
      zawwalMakroohPeriod: 10,
      sunsetMakroohPeriod: 15,

      // Screen Blanking Settings - Fajr
      blankScreenDuringFajr: false,
      fajrBlankStartOffset: 0,
      fajrBlankDuration: 0,

      // Screen Blanking Settings - Dhuhr
      blankScreenDuringDhuhr: false,
      dhuhrBlankStartOffset: 0,
      dhuhrBlankDuration: 0,

      // Screen Blanking Settings - Asar
      blankScreenDuringAsar: false,
      asarBlankStartOffset: 0,
      asarBlankDuration: 0,

      // Screen Blanking Settings - Maghrib
      blankScreenDuringMaghrib: false,
      maghribBlankStartOffset: 0,
      maghribBlankDuration: 0,

      // Screen Blanking Settings - Isha
      blankScreenDuringIsha: false,
      ishaBlankStartOffset: 0,
      ishaBlankDuration: 0,

      // Screen Blanking Settings - Jummah
      blankScreenDuringJummah: false,
      jummahBlankStartOffset: 0,
      jummahBlankDuration: 0,

      // Display Settings
      changeIslamicDateAtMaghrib: true,
      showEvents: true,
      eventPosterDuration: 5,
      showNextSalahChanges: true,
      nextSalahChangesDuration: 10,
      timesRefreshInterval: 60,

      // Customized Names
      customizedNames: {
        date: "Date",
        day: "Day",
        hijriDate: "Hijri Date",
        sehri: "Sehri",
        fajr: "Fajr",
        sunrise: "Sunrise",
        noon: "Noon",
        dhuhr: "Dhuhr",
        asar: "Asar",
        sunset: "Sunset",
        maghrib: "Maghrib",
        isha: "Isha",
        jummah: "Jummah",
        muharram: "Muharram",
        safar: "Safar",
        rabeeUlAwwal: "Rabee-ul-Awwal",
        rabeeUlAkhir: "Rabee-ul-Akhir",
        jamadahAlAwwal: "Jamadah Al-Awwal",
        jamadahAlAkhir: "Jamadah Al-Akhir",
        rajab: "Rajab",
        shaban: "Shaban",
        ramadhan: "Ramadhan",
        shawwal: "Shawwal",
        dhulQadah: "Dhul-Qadah",
        dhulHijjah: "Dhul-Hijjah",
      },
    } as ScreenData;
  }
}
